import React, { useState, useEffect } from 'react';
import { LayoutList, Plus, Trash2, Calendar, Trophy, Lock, X, Crown } from 'lucide-react';
import { collection, query, where, orderBy, onSnapshot, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';
import { SuperBoard } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { SuperBoardManager } from './SuperBoardManager';

interface Props {
  userProfile: { uid: string; displayName: string } | null;
  config: any;
  onRequireLogin?: () => void;
}

export function ToolsDashboard({ userProfile, config, onRequireLogin }: Props) {
  const [boards, setBoards] = useState<SuperBoard[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [activeBoard, setActiveBoard] = useState<SuperBoard | null>(null);

  useEffect(() => {
    if (!userProfile?.uid) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, getCollectionPath('super_boards')),
      where('creatorId', '==', userProfile.uid),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      setBoards(snap.docs.map(d => ({ id: d.id, ...d.data() } as SuperBoard)));
      setLoading(false);
    }, (err) => {
      console.error("Error loading boards", err);
      setLoading(false);
    });

    return () => unsub();
  }, [userProfile?.uid]);

  const handleCreateBoard = async (type: 'super8' | 'super12') => {
    if (!userProfile?.uid) {
      onRequireLogin?.();
      return;
    }
    try {
      const id = `sb_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const newBoard: SuperBoard = {
        id,
        creatorId: userProfile.uid,
        type,
        title: `${type === 'super8' ? 'Super 8' : 'Super 12'}`,
        date: new Date().toISOString().split('T')[0],
        createdAt: Date.now(),
        players: Array(type === 'super8' ? 8 : 12).fill(''),
      };

      await setDoc(doc(db, getCollectionPath('super_boards'), id), newBoard);
      setIsCreating(false);
    } catch (err) {
      console.error("Error creating board", err);
      alert("Hubo un error al crearlo.");
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = await (window as any).confirm('¿Seguro que deseas eliminarlo?');
    if (confirmed) {
      try {
        await deleteDoc(doc(db, getCollectionPath('super_boards'), id));
      } catch (err) {
        console.error("Error deleting", err);
      }
    }
  };

  if (activeBoard) {
    return (
      <SuperBoardManager 
        board={activeBoard} 
        onClose={() => setActiveBoard(null)} 
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-500 max-w-4xl mx-auto w-full px-2">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-2">
            <LayoutList className="text-yellow-500" />
            Mis Super 8 / 12
          </h2>
          <p className="text-sm text-white/50">Gestioná torneos express privados</p>
        </div>
        <button
          onClick={() => {
            if (!userProfile?.uid) {
              onRequireLogin?.();
              return;
            }
            setIsCreating(true);
          }}
          className="flex items-center gap-1 bg-yellow-500 text-black px-4 py-2 rounded-xl font-bold uppercase tracking-wider text-sm hover:scale-105 transition-all shadow-lg"
        >
          <Plus size={18} />
          <span className="hidden sm:inline">Nuevo S8 / S12</span>
        </button>
      </div>

      <AnimatePresence>
        {isCreating && (
          <motion.div
            initial={{ opacity: 0, y: -20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -20, height: 0 }}
            className="bg-[#1b1d26] border border-white/10 rounded-2xl p-5 mb-6 overflow-hidden"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white">¿Qué formato querés crear?</h3>
              <button onClick={() => setIsCreating(false)} className="p-1 hover:bg-white/10 rounded-full text-white/50">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleCreateBoard('super8')}
                className="flex flex-col items-center gap-3 p-4 rounded-xl bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <span className="font-black text-violet-400 text-xl">S8</span>
                </div>
                <div className="text-center">
                  <span className="block font-bold text-violet-300">Super 8</span>
                  <span className="text-[10px] text-white/40 uppercase">8 Jugadores</span>
                </div>
              </button>
              <button
                onClick={() => handleCreateBoard('super12')}
                className="flex flex-col items-center gap-3 p-4 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-sky-500/20 flex items-center justify-center">
                  <span className="font-black text-sky-400 text-xl">S12</span>
                </div>
                <div className="text-center">
                  <span className="block font-bold text-sky-300">Super 12</span>
                  <span className="text-[10px] text-white/40 uppercase">12 Jugadores</span>
                </div>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-white/20 border-t-yellow-500 rounded-full animate-spin" />
        </div>
      ) : boards.length === 0 ? (
        <div className="text-center py-20 bg-white/5 rounded-3xl border border-white/10 flex flex-col items-center">
          <LayoutList size={48} className="mx-auto text-white/20 mb-4" />
          <h3 className="text-base font-black text-white/60 uppercase tracking-wider mb-2">No tenés Super 8 / 12 creados</h3>
          <p className="text-sm text-white/40 mb-6">Crea uno para gestionar un torneo express privado.</p>
          <button
            onClick={() => {
              if (!userProfile?.uid) {
                onRequireLogin?.();
                return;
              }
              setIsCreating(true);
            }}
            className="inline-flex items-center gap-2 bg-yellow-500 text-black px-6 py-3 rounded-xl font-black uppercase tracking-wider text-sm hover:scale-105 transition-all shadow-lg"
          >
            <Plus size={18} />
            Crear Super
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {boards.map(board => {
            let winnerName = null;
            if (board.completedAt) {
              const winnerPId = board.type === 'super12' ? board.super12Data?.winnerPairId : board.super8Data?.winnerPairId;
              if (winnerPId) {
                const p1 = board.players[(winnerPId - 1) * 2];
                const p2 = board.players[(winnerPId - 1) * 2 + 1];
                winnerName = [p1, p2].filter(Boolean).join(' / ');
              }
            }

            return (
            <div
              key={board.id}
              onClick={() => setActiveBoard(board)}
              className="bg-[#1e2538]/50 border border-white/10 hover:border-white/20 rounded-2xl p-4 cursor-pointer transition-all hover:scale-[1.02] flex flex-col group relative"
            >
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <div className="bg-black/40 p-1.5 rounded-lg border border-white/5" title="Privado (Solo tú puedes verlo)">
                  <Lock size={12} className="text-white/40" />
                </div>
                <button
                  onClick={(e) => handleDelete(board.id, e)}
                  className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors border border-red-500/20 opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                >
                  <Trash2 size={12} />
                </button>
              </div>

              <div className="mb-4 pr-16">
                <h3 className="font-black text-white uppercase text-lg leading-tight truncate">{board.title}</h3>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mt-2 inline-block ${
                  board.type === 'super12' ? 'bg-sky-500/15 text-sky-400 border border-sky-500/20' : 'bg-violet-500/15 text-violet-400 border border-violet-500/20'
                }`}>
                  {board.type === 'super12' ? 'Super 12' : 'Super 8'}
                </span>
              </div>

              {winnerName && (
                <div className="mt-2 mb-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center gap-2">
                  <Crown size={14} className="text-yellow-500 shrink-0" />
                  <div className="flex flex-col">
                    <span className="text-[8px] font-black uppercase tracking-widest text-yellow-500/70">Ganadores</span>
                    <span className="text-[11px] font-bold text-white truncate max-w-[150px]">{winnerName}</span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 mt-auto pt-4 border-t border-white/5 text-xs text-white/50">
                <div className="flex items-center gap-1">
                  <Calendar size={12} />
                  <span>{board.date.split('-').reverse().join('/')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Trophy size={12} className={board.completedAt ? "text-yellow-500" : ""} />
                  <span>{board.completedAt ? 'Finalizado' : 'En Curso'}</span>
                </div>
              </div>
            </div>
          )})}
        </div>
      )}
    </div>
  );
}
